// Resilient consume runner (modules/04 web-client). The later joiner (always the call
// receiver) consumes the producers already present via getExistingProducers, awaited inside
// start(). A single transient failure there (recv transport / producer not yet ready) must
// NOT abort the whole call setup and surface a fatal red toast while leaving every other
// producer unconsumed - the earlier joiner (caller) consumes via the fire-and-forget
// newProducer path and tolerates such failures, so the receiver must be equally resilient.
//
// This pure helper retries a consume task a few times with backoff and resolves a success
// flag instead of throwing. It is injected with sleep/shouldStop so it is unit-testable with
// fakes (no real audio device, no mediasoup).

export interface RetryOptions {
  /** Total attempts before giving up (default 3). */
  attempts?: number;
  /** Backoff before the next attempt, in ms, given the 1-based attempt that just failed. */
  delayMs?: (failedAttempt: number) => number;
  /** Sleep used between attempts (default setTimeout); overridable in tests. */
  sleep?: (ms: number) => Promise<void>;
  /** Called once with the final error when all attempts fail (e.g. console.warn). */
  onGiveUp?: (error: unknown) => void;
  /** Abort early (e.g. the media client closed mid-startup); returns false without retrying. */
  shouldStop?: () => boolean;
}

/**
 * Run `task` up to `attempts` times. Resolves true on the first success, false if every
 * attempt throws or `shouldStop()` becomes true. Never throws: a failing remote consume must
 * not tear down the rest of the call.
 */
export async function runWithRetries(task: () => Promise<void>, opts: RetryOptions = {}): Promise<boolean> {
  const attempts = opts.attempts ?? 3;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const delayMs = opts.delayMs ?? ((failedAttempt: number) => 150 * failedAttempt);

  for (let attempt = 1; attempt <= attempts; attempt++) {
    if (opts.shouldStop?.()) return false;
    try {
      await task();
      return true;
    } catch (error) {
      if (opts.shouldStop?.()) return false;
      if (attempt >= attempts) {
        opts.onGiveUp?.(error);
        return false;
      }
      await sleep(delayMs(attempt));
    }
  }
  return false;
}
